import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import type { StaffBookingSummaryResponse } from "@repo/api-client";
import { useVenue } from "../shell/VenueContext";
import { ChannelBadge, StatusBadge } from "./Badges";
import { bookingWhen, guestName, isNum } from "./format";
import { affordancesFor, canChargeNoShowFee, toBookingStatus } from "./status";
import { dangerButton, primaryButton, secondaryButton } from "./styles";

/**
 * The booking peek — the detail surface opened from any view (list row, floor table, timeline block).
 * Its action buttons DERIVE from status (R1, BK-8): edit/cancel only on live bookings, "recreate"
 * only on terminal ones, and never a cancel on a SEATED booking. The screen wires the callbacks to
 * the edit / cancel / manual-entry dialogs.
 */
export function BookingPeekPanel({
  booking,
  onEdit,
  onCancel,
  onRecreate,
  onNoShowFee,
  onClose,
}: {
  booking: StaffBookingSummaryResponse;
  onEdit: () => void;
  onCancel: () => void;
  onRecreate: () => void;
  /** Open the no-show charge dialog (offered only when the booking is chargeable, R5). */
  onNoShowFee?: () => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { venue } = useVenue();
  const status = toBookingStatus(booking.status);
  const aff = status ? affordancesFor(status) : null;

  return (
    <aside
      aria-label={t("bookings.peek.title")}
      className={css({
        borderWidth: "1px",
        borderColor: "border.default",
        rounded: "lg",
        p: "4",
        bg: "bg.default",
        display: "flex",
        flexDirection: "column",
        gap: "3",
        minW: "0",
      })}
    >
      <div className={css({ display: "flex", justifyContent: "space-between", gap: "2" })}>
        <h2 className={css({ fontSize: "md", fontWeight: "semibold" })}>
          {guestName(booking, t("bookings.noName"))}
        </h2>
        <button
          type="button"
          className={closeBtn}
          onClick={onClose}
          aria-label={t("bookings.action.close")}
        >
          ✕
        </button>
      </div>

      <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
        <StatusBadge status={booking.status} />
        <ChannelBadge channel={booking.channel} />
      </div>

      <dl
        className={css({
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: "1",
          fontSize: "sm",
        })}
      >
        <dt className={dt}>{t("bookings.field.time")}</dt>
        <dd>
          {isNum(booking.startSec)
            ? bookingWhen(booking.startSec, venue.timeZone, i18n.language)
            : "—"}
        </dd>
        <dt className={dt}>{t("bookings.field.party")}</dt>
        <dd>{booking.partySize ?? "—"}</dd>
        {booking.guestPhone ? (
          <>
            <dt className={dt}>{t("bookings.field.phone")}</dt>
            <dd>{booking.guestPhone}</dd>
          </>
        ) : null}
        {booking.zonePreference ? (
          <>
            <dt className={dt}>{t("bookings.field.zonePreference")}</dt>
            <dd>{booking.zonePreference}</dd>
          </>
        ) : null}
        {booking.allergyNote ? (
          <>
            <dt className={dt}>{t("bookings.field.allergyNote")}</dt>
            <dd>{booking.allergyNote}</dd>
          </>
        ) : null}
      </dl>

      {booking.blacklistFlagged ? (
        <p role="note" className={css({ fontSize: "xs", color: "booking.pending.fg" })}>
          {t("bookings.peek.flagged")}
        </p>
      ) : null}

      {aff ? (
        <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
          {aff.canEdit ? (
            <button type="button" className={secondaryButton} onClick={onEdit}>
              {t("bookings.action.edit")}
            </button>
          ) : null}
          {aff.canCancel ? (
            <button type="button" className={secondaryButton} onClick={onCancel}>
              {t("bookings.action.cancel")}
            </button>
          ) : null}
          {aff.canRecreate ? (
            <button type="button" className={primaryButton} onClick={onRecreate}>
              {t("bookings.action.recreate")}
            </button>
          ) : null}
          {onNoShowFee && status && canChargeNoShowFee(status) ? (
            <button
              type="button"
              className={dangerButton}
              onClick={onNoShowFee}
              data-testid="peek-noshow-fee"
            >
              {t("app.noShowCharge.action")}
            </button>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
}

const dt = css({ color: "fg.muted", pr: "3" });
const closeBtn = css({
  fontSize: "sm",
  color: "fg.muted",
  px: "1",
  _hover: { color: "fg.default" },
});
